import { useGetSecretById } from '@/hooks/external-keys/external-keys';
import { useWebhookTest } from '@/pages/agents/api';
import { TriggerType, TriggerWebhook } from '@d-id/types';
import { AxiosError } from 'axios';
import { createContext, useContext, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { WebhookFormValues } from '../types';
import {
    isNewFormat,
    parseFormDataToTriggerWebhook,
    parseTriggerWebhookToFormData,
    parseTriggerWebhookToFormDataNew,
} from '../utils/parse-data';

const DEFAULT_VALUES: WebhookFormValues = {
    url: '',
    headers: [],
    queryParams: [],
    auth: {
        type: 'none',
    },
};

interface WebhookComponentContextType {
    modalOpen: boolean;
    setModalOpen: (open: boolean) => void;

    mode: 'edit' | 'create';

    activeTab: 'configure' | 'test';
    setActiveTab: (tab: 'configure' | 'test') => void;

    testResults: string | null;

    runTest: () => Promise<void>;
    onWebhookSave: (triggerWebhook: TriggerWebhook) => void;
    onWebhookRemove: (index?: number) => void;

    webhookIndex: number | null;
    onWebhookEdit: (webhook: TriggerWebhook, index: number) => void;

    webhooks: TriggerWebhook[];
    isLoadingSecret: boolean;
    secretLoadError: Error | null;
}

export const WebhookComponentContext = createContext<WebhookComponentContextType>({
    modalOpen: false,
    setModalOpen: () => {},
    mode: 'create',
    activeTab: 'configure',
    setActiveTab: () => {},
    testResults: null,
    runTest: async () => {},
    onWebhookSave: () => {},
    onWebhookRemove: () => {},
    webhookIndex: null,
    onWebhookEdit: () => {},
    webhooks: [],
    isLoadingSecret: false,
    secretLoadError: null,
});

export const useWebhookComponentContext = () => {
    return useContext(WebhookComponentContext);
};

interface Props {
    children: React.ReactNode;
    onSave: (triggerWebhook: TriggerWebhook | null, index: number) => void;
    webhooks: TriggerWebhook[];
}

export const WebhookComponentProvider = ({ children, onSave, webhooks }: Props) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<'configure' | 'test'>('configure');
    const [mode, setMode] = useState<'edit' | 'create'>('create');
    const [testResults, setTestResults] = useState<string>('{}');
    const [webhookIndex, setWebhookIndex] = useState<number | null>(null);
    const [pendingWebhook, setPendingWebhook] = useState<TriggerWebhook | null>(null);

    const { mutate: testWebhook } = useWebhookTest();

    const secretId = pendingWebhook?.auth?.secret_id;
    const isNewFormatWebhook = pendingWebhook ? isNewFormat(pendingWebhook.auth) : false;
    const { data: secret, isLoading: isLoadingSecret, error: secretLoadError } = useGetSecretById(secretId, isNewFormatWebhook);

    const formMethods = useForm<WebhookFormValues>({
        defaultValues: DEFAULT_VALUES,
        mode: 'onChange',
    });

    const handleReset = () => {
        setActiveTab('configure');
        setTestResults(' ');
        setWebhookIndex(null);
        setPendingWebhook(null);
        formMethods.reset(DEFAULT_VALUES);
    };

    const handleSetOpen = (newOpenState: boolean) => {
        if (!newOpenState) {
            handleReset();
        }
        setMode('create');
        setModalOpen(newOpenState);
    };

    const onWebhookEdit = (webhook: TriggerWebhook, index: number) => {
        setWebhookIndex(index);
        setPendingWebhook(webhook);

        if (!isNewFormat(webhook.auth)) {
            const values = parseTriggerWebhookToFormData(webhook);
            formMethods.reset(values);
        }

        handleSetOpen(true);
        setMode('edit');
    };

    useEffect(() => {
        if (!pendingWebhook || !isNewFormat(pendingWebhook.auth)) {
            return;
        }
        if (secret && !isLoadingSecret) {
            const values = parseTriggerWebhookToFormDataNew(pendingWebhook, secret);
            formMethods.reset(values);
            setPendingWebhook(null);
        } else if (secretLoadError && !isLoadingSecret) {
            formMethods.reset({ ...DEFAULT_VALUES, auth: pendingWebhook.auth });
            setPendingWebhook(null);
        }
    }, [secret, secretLoadError, isLoadingSecret, pendingWebhook, formMethods]);

    const onWebhookSave = (triggerWebhook: TriggerWebhook) => {
        onSave(triggerWebhook, webhookIndex ?? -1);
    };

    const onWebhookRemove = (index?: number) => {
        onSave(null, webhookIndex ?? index ?? -1);

        handleSetOpen(false);
    };

    const runTest = async () => {
        return new Promise<void>((resolve, reject) => {
            const values = formMethods.getValues();

            const singleWebhookData = parseFormDataToTriggerWebhook(values);

            const triggersObject = {
                event_type: TriggerType.chatEnd as const,
                url: values.url,
                auth: singleWebhookData.auth ?? undefined,
                headers: singleWebhookData.headers ?? undefined,
            };

            testWebhook(triggersObject, {
                onSuccess: response => {
                    setTestResults(JSON.stringify(response, null, 2));
                    resolve();
                },
                onError: error => {
                    setTestResults(JSON.stringify({ error: (error as AxiosError)?.response?.data }, null, 2));
                    reject();
                },
            });
        });
    };

    return (
        <FormProvider {...formMethods}>
            <WebhookComponentContext.Provider
                value={{
                    modalOpen,
                    setModalOpen: handleSetOpen,
                    mode,
                    activeTab,
                    setActiveTab,
                    testResults,
                    runTest,
                    onWebhookSave,
                    onWebhookRemove,
                    webhookIndex,
                    onWebhookEdit,
                    webhooks,
                    isLoadingSecret,
                    secretLoadError: secretLoadError as Error | null,
                }}>
                {children}
            </WebhookComponentContext.Provider>
        </FormProvider>
    );
};
